import React, { useState, useRef } from "react";
import { motion } from "framer-motion";
import { Calendar, Sparkles, Pencil, Check, X, Camera } from "lucide-react";
import { api, daysBetween } from "@/lib/api";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }),
};

const HERO_IMG =
  "https://images.unsplash.com/photo-1541089404510-5c9a779841fc?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxjdXRlJTIwY291cGxlJTIwbGlmZXN0eWxlfGVufDB8fHx8MTc4MDU1NTU5OHww&ixlib=rb-4.1.0&q=85";

export default function Dashboard({ config, setConfig, surprise }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(config.anniversary);
  const [uploading, setUploading] = useState(false);
  const [editCap, setEditCap] = useState(false);
  const [capDraft, setCapDraft] = useState(config.hero_caption || "my favorite person");
  const fileRef = useRef(null);

  const days = daysBetween(config.anniversary);
  const years = (days / 365).toFixed(1);

  const saveDate = async () => {
    const { data } = await api.put("/config", { anniversary: draft });
    setConfig(data);
    setEditing(false);
  };

  const onHeroFile = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const reader = new FileReader();
    reader.onload = async () => {
      const { data } = await api.put("/config", { hero_image: reader.result });
      setConfig(data);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const saveCaption = async () => {
    const value = capDraft.trim() || "my favorite person";
    const { data } = await api.put("/config", { hero_caption: value });
    setConfig(data);
    setCapDraft(value);
    setEditCap(false);
  };

  return (
    <section id="home" className="max-w-6xl mx-auto px-6 pt-12 pb-8">
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
        className="font-hand text-3xl text-[#D95F59] mb-2"
      >
        Hi my love,
      </motion.p>
      <motion.h1
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        className="font-display text-5xl md:text-6xl tracking-tight leading-[1.05] mb-8"
      >
        This little corner of the world <br className="hidden md:block" />
        is just for <span className="italic text-[#D95F59]">us</span>.
      </motion.h1>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 auto-rows-[minmax(0,auto)]">
        {/* Days together - big */}
        <motion.div
          custom={0}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          data-testid="days-together-card"
          className="md:col-span-2 card-paper rounded-3xl p-8 flex flex-col justify-between min-h-[240px] relative overflow-hidden"
        >
          <div className="flex items-center gap-2 text-[#756D6A] text-sm">
            <Calendar className="w-4 h-4" /> Days together
          </div>
          <div>
            <div className="font-display text-7xl md:text-8xl tracking-tight text-[#2A2726]">
              {days.toLocaleString()}
            </div>
            <p className="font-hand text-2xl text-[#D95F59]">
              that's about {years} years of you & me ✨
            </p>
          </div>
          <div className="flex items-center gap-3 text-sm text-[#756D6A]">
            {editing ? (
              <>
                <input
                  type="date"
                  data-testid="anniversary-input"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="rounded-full border border-[#EADEDA] px-3 py-1 bg-[#FFF5F5]"
                />
                <button data-testid="save-anniversary" onClick={saveDate} className="text-[#8C9A86]">
                  <Check className="w-5 h-5" />
                </button>
                <button onClick={() => setEditing(false)} className="text-[#756D6A]">
                  <X className="w-5 h-5" />
                </button>
              </>
            ) : (
              <button
                data-testid="edit-anniversary"
                onClick={() => setEditing(true)}
                className="flex items-center gap-1 hover:text-[#2A2726] transition-colors"
              >
                <Pencil className="w-3.5 h-3.5" /> since {config.anniversary}
              </button>
            )}
          </div>
        </motion.div>

        {/* Hero polaroid - editable */}
        <motion.div
          custom={1}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          className="card-paper rounded-3xl p-3 rotate-2 hover:rotate-0 transition-transform duration-500"
        >
          <button
            type="button"
            data-testid="edit-hero-photo"
            onClick={() => fileRef.current?.click()}
            className="block w-full relative group rounded-2xl overflow-hidden"
          >
            <img
              src={config.hero_image || HERO_IMG}
              alt="us"
              className="w-full h-auto max-h-[460px] object-contain rounded-2xl bg-[#FFF5F5]"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/35 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 text-white text-sm font-medium bg-[#D95F59] px-4 py-2 rounded-full">
                <Camera className="w-4 h-4" /> {uploading ? "Uploading…" : "Change photo"}
              </span>
            </div>
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onHeroFile}
            data-testid="hero-file-input"
          />
          {editCap ? (
            <div className="flex items-center gap-1 mt-2">
              <input
                data-testid="hero-caption-input"
                value={capDraft}
                onChange={(e) => setCapDraft(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && saveCaption()}
                autoFocus
                maxLength={40}
                className="flex-1 font-hand text-2xl text-center bg-[#FFF5F5] border border-[#EADEDA] rounded-xl px-2 py-1 outline-none focus:border-[#D95F59]"
              />
              <button data-testid="save-hero-caption" onClick={saveCaption} className="text-[#8C9A86]">
                <Check className="w-5 h-5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              data-testid="edit-hero-caption"
              onClick={() => { setCapDraft(config.hero_caption || "my favorite person"); setEditCap(true); }}
              className="group/cap flex items-center justify-center gap-1.5 w-full mt-2"
            >
              <span className="font-hand text-2xl text-[#2A2726]">
                {config.hero_caption || "my favorite person"}
              </span>
              <Pencil className="w-3.5 h-3.5 text-[#756D6A] opacity-0 group-hover/cap:opacity-100 transition-opacity" />
            </button>
          )}
        </motion.div>

        {/* Daily surprise */}
        <motion.div
          custom={2}
          variants={fadeUp}
          initial="hidden"
          animate="show"
          data-testid="daily-surprise-card"
          className="md:col-span-3 rounded-3xl p-8 bg-[#FFF5F5] border border-[#EADEDA] flex items-start gap-4"
        >
          <div className="shrink-0 w-11 h-11 rounded-full bg-[#D95F59] flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-widest text-[#756D6A] mb-1">
              Your surprise for today
            </p>
            <p className="font-display text-2xl md:text-3xl italic leading-snug">
              {surprise || "Loading something sweet…"}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
